import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
    AlertBox,
    Box,
    Button,
    FlexBox,
    FormField,
    FormInputGroup,
    FormLabel,
    Icon,
    Typography,
} from '@vp/swan';
import { TimezoneSelector, useIdentityContext } from '@99designs/design-services-common';
import {
    ANY_DESIGNER_CALENDAR_ID,
    DateSelector,
    TimeSelector,
    isAppointmentValid,
    parseAppointmentValue,
    serializeAppointmentValue,
    useConfirmDesignLiveTimeIsAvailable,
} from '@99designs/feature-design-services-brief';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useDesignLivePreviousDesignersForChatBriefQuery } from '../../../graphql/design-live-previous-designers.generated';
import { WizardOptionList } from '../../WizardStep/WizardOptionList';
import { WizardStep } from '../../WizardStep/WizardStep';
import { ChatDesignerSelector } from './ChatDesignerSelector';
import type { PreviousDesigner } from '@99designs/feature-design-services-brief';
import type { WizardOptionDefinition } from '../../WizardStep/WizardOptionList';
import type { WizardStepDefinition } from '../../WizardStep/WizardStep';
import type { ReviewMethodOption, ReviewMethodOptions } from './parseOptions';

// Error message functions — called at render time so __() picks up the active locale.
const unavailableError = () =>
    __(
        'Your selected DesignLive session is no longer available. Please select a new day and time.',
        {
            _context:
                'Error shown when the chosen DesignLive appointment slot is no longer available',
        }
    );

const checkError = () =>
    __(
        'There was an error checking the availability of your selected DesignLive session. Please try again.',
        {
            _context:
                'Error shown when the availability check for the chosen DesignLive slot fails',
        }
    );

export interface ReviewMethodSelectorViewProps {
    options: ReviewMethodOptions;
    onSubmit: (content: string) => void;
    /** One-time: send a help request through the same onSubmit channel. */
    onRequestHelp: () => void;
}

function toWizardOption(option: ReviewMethodOption): WizardOptionDefinition {
    return {
        value: option.value,
        icon: (
            <Icon iconType={option.value === 'DesignLive' ? 'videoChat' : 'mail'} size="standard" />
        ),
        label: option.name,
    };
}

// True when the customer has just come back from the designer-matching sign-in redirect.
function returnedFromDesignerSignIn(): boolean {
    try {
        return new URLSearchParams(window.location.search).get('matchWithPreviousDesigner') === '1';
    } catch {
        return false;
    }
}

/**
 * Full review-method selector — a port of the legacy ReviewMethodSelector into the Phase 2
 * generative-UI system.
 *
 * Step 1: Standard vs DesignLive choice.
 * Step 2 (DesignLive only): Designer selection (optional) + Timezone + date + time slot +
 *   availability validation.
 *
 * Wire format on submit (matching the legacy completionFor payload):
 *   Standard:   { requestType: 'Standard' }
 *   DesignLive: { requestType: 'DesignLive', appointmentTime: <JSON envelope> }
 */
export function ReviewMethodSelectorView({
    options,
    onSubmit,
    onRequestHelp,
}: ReviewMethodSelectorViewProps) {
    const timeFieldId = useId();
    const designerSelectorId = useId();
    const { locale } = usePage();
    const { isSignedIn, SignIn } = useIdentityContext();
    const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
    const preselectedAppt = parseAppointmentValue(options.preselectedTime ?? null);
    const startAtDesignerMatching = returnedFromDesignerSignIn();

    const [activeStep, setActiveStep] = useState(startAtDesignerMatching ? 2 : 1);
    const [selectedMethod, setSelectedMethod] = useState<'Standard' | 'DesignLive' | null>(
        startAtDesignerMatching ? 'DesignLive' : options.preselectedMethod ?? null
    );
    const [timezone, setTimezone] = useState(options.preselectedTimezone ?? browserTimezone);
    const [date, setDateInternal] = useState<string | null>(options.preselectedDate ?? null);
    const [time, setTime] = useState(preselectedAppt.time ?? '');
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [helpRequested, setHelpRequested] = useState(false);

    // ── Designer selection state ───────────────────────────────────────────────
    // calendarId: the raw Acuity calendar ID the customer/AI has selected (or ANY_DESIGNER_CALENDAR_ID
    // = 0 for "no specific designer").
    const initialCalendarId =
        preselectedAppt.calendarId ?? options.calendarId ?? ANY_DESIGNER_CALENDAR_ID;
    const [calendarId, setCalendarId] = useState(initialCalendarId);
    const [matchWithPreviousDesigner, setMatchWithPreviousDesigner] = useState(
        startAtDesignerMatching ||
            (preselectedAppt.matchWithPreviousDesigner ??
                initialCalendarId !== ANY_DESIGNER_CALENDAR_ID)
    );

    const {
        data: designersData,
        loading: isLoadingDesigners,
        error: designersError,
    } = useDesignLivePreviousDesignersForChatBriefQuery({
        skip: !isSignedIn,
        errorPolicy: 'all',
    });

    const previousDesigners: PreviousDesigner[] =
        designersData?.designLivePreviousDesigners.map((d) => ({
            acuityCalendarId: d.acuityCalendarId,
            designerName: d.designerName,
        })) ?? [];

    const designersResolved = Boolean(designersData);

    const resolveSpecificCalendarId = (id: number): number => {
        if (previousDesigners.some((d) => d.acuityCalendarId === id)) return id;
        return previousDesigners[0]?.acuityCalendarId ?? ANY_DESIGNER_CALENDAR_ID;
    };

    const selectedCalendarId = !matchWithPreviousDesigner
        ? ANY_DESIGNER_CALENDAR_ID
        : designersResolved
        ? resolveSpecificCalendarId(calendarId)
        : calendarId;

    const effectiveCalendarId =
        selectedCalendarId !== ANY_DESIGNER_CALENDAR_ID ? selectedCalendarId : undefined;

    const selectedDesignerName =
        previousDesigners.find((d) => d.acuityCalendarId === selectedCalendarId)?.designerName ??
        (selectedCalendarId === initialCalendarId ? preselectedAppt.designerName : undefined);

    const { checkAvailability } = useConfirmDesignLiveTimeIsAvailable(
        date,
        time,
        timezone,
        effectiveCalendarId
    );

    // Clear stale validation error whenever the user changes their selection.
    useEffect(() => {
        setValidationError(null);
    }, [date, time, timezone]);

    // Clear the chosen date/time slot
    const clearSlot = useCallback(() => {
        setDateInternal(null);
        setTime('');
    }, []);

    // Once the designer list has resolved, persist the reconciled calendarId — but only while
    // matchWithPreviousDesigner is true, or a toggle-off would wipe the remembered pick.
    useEffect(() => {
        if (!designersResolved || !matchWithPreviousDesigner || selectedCalendarId === calendarId) {
            return;
        }
        if (calendarId !== ANY_DESIGNER_CALENDAR_ID) {
            clearSlot();
        }
        setCalendarId(selectedCalendarId);
    }, [designersResolved, matchWithPreviousDesigner, selectedCalendarId, calendarId, clearSlot]);

    const handleTimezoneChange = (newTimezone: string) => {
        setTimezone(newTimezone);
        clearSlot();
    };

    const setDate = (newDate: string | null) => {
        if (date !== newDate) setTime('');
        setDateInternal(newDate);
    };

    const handleToggleChange = (activated: boolean) => {
        // Keep the raw calendarId in memory even while inactive, so re-toggling on
        // restores the same designer instead of forgetting the previous pick — only
        // the *effective* calendar (what's actually applied to availability) needs to
        // change here, and only that should invalidate an already-picked slot.
        const nextSpecificCalendarId = designersResolved
            ? resolveSpecificCalendarId(calendarId)
            : calendarId;
        const nextEffectiveCalendarId =
            activated && nextSpecificCalendarId !== ANY_DESIGNER_CALENDAR_ID
                ? nextSpecificCalendarId
                : undefined;
        if (nextEffectiveCalendarId !== effectiveCalendarId) {
            clearSlot();
        }
        setMatchWithPreviousDesigner(activated);
    };

    const handleCalendarChange = (newCalendarId: number) => {
        if (newCalendarId !== calendarId) {
            setCalendarId(newCalendarId);
            clearSlot();
        }
    };

    const handleRequestHelp = () => {
        setHelpRequested(true);
        onRequestHelp();
    };

    // Step 1 footer action: submit Standard immediately, or advance to step 2 for DesignLive.
    const handleStep1Continue = () => {
        if (!selectedMethod) return;
        if (selectedMethod === 'Standard') {
            onSubmit(JSON.stringify({ type: 'review-method', data: { requestType: 'Standard' } }));
        } else {
            setActiveStep(2);
        }
    };

    const handleContinue = async () => {
        if (!date || !time) return;

        setIsValidating(true);
        try {
            if (!isAppointmentValid(time)) {
                setValidationError(unavailableError());
                return;
            }

            const isAvailable = await checkAvailability();
            if (isAvailable === false) {
                setValidationError(unavailableError());
                return;
            }
            if (isAvailable === null) {
                setValidationError(checkError());
                return;
            }

            onSubmit(
                JSON.stringify({
                    type: 'review-method',
                    data: {
                        requestType: 'DesignLive',
                        appointmentTime: serializeAppointmentValue(
                            time,
                            timezone,
                            effectiveCalendarId,
                            selectedDesignerName,
                            matchWithPreviousDesigner
                        ),
                    },
                })
            );
        } finally {
            setIsValidating(false);
        }
    };

    const methodStep1Title = options.title ?? __('How would you like to work with your designer?');

    const wizardOptions: WizardOptionDefinition[] = options.options.map(toWizardOption);

    const step1: WizardStepDefinition = {
        title: methodStep1Title,
        // Disable the chevron until a method is chosen. Once DesignLive is selected the
        // chevron and the footer Continue button are equivalent — both just advance to
        // step 2 (Standard submits immediately from the footer button instead, and has
        // no step 2 to navigate to).
        nextDisabled: !selectedMethod,
        content: (
            <WizardOptionList
                aria-label={methodStep1Title}
                options={wizardOptions}
                selectedValue={selectedMethod ?? ''}
                onSelectedValueChange={(v) => setSelectedMethod(v as 'Standard' | 'DesignLive')}
            />
        ),
        footer: (
            <FlexBox justifyContent="space-between" alignItems="center">
                <Button
                    skin="tertiary"
                    iconPosition="left"
                    onClick={handleRequestHelp}
                    disabled={helpRequested}
                >
                    <Icon iconType="aiSupport" />
                    {__('Help me answer')}
                </Button>
                <Button skin="secondary" onClick={handleStep1Continue} disabled={!selectedMethod}>
                    {__('Continue')}
                </Button>
            </FlexBox>
        ),
    };

    const step2: WizardStepDefinition = {
        title: __('Book your DesignLive session'),
        content: (
            <Box>
                <Box
                    backgroundColor="standard"
                    paddingX={5}
                    paddingY={4}
                    mb={4}
                    style={{
                        border: '1px solid var(--swan-sem-color-border-subtle)',
                        borderRadius: 'var(--swan-sem-border-radius-standard)',
                    }}
                >
                    <ChatDesignerSelector
                        id={designerSelectorId}
                        isSignedIn={isSignedIn}
                        onSignIn={() =>
                            // The return key brings the customer back to this step with matching
                            // still on, rather than the top of the wizard — read on mount above.
                            SignIn([{ key: 'matchWithPreviousDesigner', value: '1' }], {
                                trackingCaller: 'chat-brief-design-live-designer-selection',
                            })
                        }
                        matchWithPreviousDesigner={matchWithPreviousDesigner}
                        onToggleChange={handleToggleChange}
                        designers={previousDesigners}
                        isLoadingDesigners={isLoadingDesigners}
                        hasDesignersError={Boolean(designersError)}
                        calendarId={selectedCalendarId}
                        setCalendarId={handleCalendarChange}
                    />
                </Box>
                <FormField mb={4}>
                    <FormLabel fontSkin="body-standard" marginBottom="4">
                        {__('Your timezone')}
                    </FormLabel>
                    <FormInputGroup marginTop={'2'}>
                        <TimezoneSelector
                            value={timezone}
                            onChange={handleTimezoneChange}
                            browserTimezone={browserTimezone}
                            locale={locale}
                        />
                    </FormInputGroup>
                </FormField>
                <FormField mb={4}>
                    <FormLabel fontSkin="body-standard" marginBottom="4">
                        {__('Select a date')}
                    </FormLabel>
                    <FormInputGroup marginTop={'2'}>
                        <DateSelector
                            timezone={timezone}
                            date={date}
                            setDate={setDate}
                            immutable={false}
                            calendarId={effectiveCalendarId}
                        />
                    </FormInputGroup>
                </FormField>
                {date !== null && (
                    <FormField mb={4}>
                        <FormLabel fontSkin="body-standard" marginBottom="4" htmlFor={timeFieldId}>
                            {__('Select a time')}
                        </FormLabel>
                        <FormInputGroup marginTop={'2'}>
                            <TimeSelector
                                timezone={timezone}
                                date={date}
                                time={time}
                                setTime={setTime}
                                immutable={false}
                                calendarId={effectiveCalendarId}
                                id={timeFieldId}
                            />
                        </FormInputGroup>
                        {isSignedIn && matchWithPreviousDesigner && (
                            <Typography fontSkin="body-small" textColor="subtle" mt={2}>
                                {selectedDesignerName
                                    ? __('Showing times when {{designerName}} is available', {
                                          designerName: selectedDesignerName,
                                          _context:
                                              'Helper text shown below the time picker when a specific designer is selected',
                                      })
                                    : __("Showing times across all designer's availability", {
                                          _context:
                                              "Helper text shown below the time picker when the customer has opted in to designer matching but hasn't chosen a specific designer yet",
                                      })}
                            </Typography>
                        )}
                    </FormField>
                )}
                {validationError && (
                    <AlertBox
                        skin="error"
                        toast={false}
                        accessibleTextForIcon={__('Error')}
                        style={{ width: '100%' }}
                    >
                        <Typography fontSkin="body-standard">{validationError}</Typography>
                    </AlertBox>
                )}
            </Box>
        ),
        footer: (
            <FlexBox justifyContent="space-between" alignItems="center">
                <Button
                    skin="tertiary"
                    iconPosition="left"
                    onClick={handleRequestHelp}
                    disabled={helpRequested}
                >
                    <Icon iconType="aiSupport" />
                    {__('Help me answer')}
                </Button>
                <Button skin="secondary" onClick={handleContinue} disabled={!time || isValidating}>
                    {__('Continue')}
                </Button>
            </FlexBox>
        ),
    };

    const steps: WizardStepDefinition[] =
        selectedMethod === 'DesignLive' ? [step1, step2] : [step1];

    return (
        <WizardStep
            steps={steps}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            previousStepLabel={__('Previous step')}
            nextStepLabel={__('Next step')}
        />
    );
}
